import {
  BAND_LABEL,
  MISSIONS,
  SCORE_MAX,
  SCORE_MIN,
  type ControlStatus,
  type PassportProtection,
  type PublicPassport,
} from '@cyberscore/contracts';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, Download, FileText, Link2Off, ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';
import { PROVENANCE_LABEL } from '../business/progress';
import { BadgeCard } from '../business/ShareScore';
import '../business/business.css';
import { fetchPublicPassport, passportDocumentUrl } from './api';

const day = (iso: string) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' }); // prettier-ignore
const STATUS: Record<ControlStatus, { label: string; tone: string }> = {
  in_place: { label: 'In place', tone: 'ok' },
  partial: { label: 'Partly', tone: 'warn' },
  missing: { label: 'Missing', tone: 'bad' },
  unknown: { label: 'Not sure', tone: '' },
  unanswered: { label: 'Not answered', tone: '' },
};
const size = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`); // prettier-ignore

/**
 * A cyber passport (task 76), for anyone with the link: no sign-in. It shows what the business agreed to
 * share, as it stood on the day it was shared, and says so. A stopped or expired link shows it isn't shared.
 */
export function PublicPassportPage({ token }: { token: string }) {
  const q = useQuery({ queryKey: ['public-passport', token], queryFn: () => fetchPublicPassport(token), retry: false }); // prettier-ignore
  const p = q.data ?? null;
  const title = p ? `${p.contents.business} · Cyber passport` : null;
  useEffect(() => {
    if (!title) return;
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);

  return (
    <div className="biz public-badge public-passport">
      <main className="pb-main pp-main">
        <div className="pb-brand">
          <span className="brand-mark" aria-hidden><ShieldCheck size={18} /></span>
          <b>CyberScore</b> <span className="muted">· Cyber passport</span>
        </div>
        {q.isPending ? (
          <section className="bcard pb-card"><p className="muted">Loading…</p></section>
        ) : p ? (
          <Shown p={p} token={token} />
        ) : (
          <section className="bcard pb-card empty-card">
            <span className="hicon big"><Link2Off size={22} /></span>
            <h2>{q.isError ? 'The passport could not be loaded' : 'This passport link is not shared'}</h2>
            <p className="muted">{q.isError ? q.error.message : 'The business may have stopped sharing it, its date may have passed, or the link is mistyped. Ask them for a new link.'}</p>
          </section>
        )}
        <p className="muted pb-foot">CyberScore measures how well a business is protected against the cyber losses insurers see most. What is shown here is what the business chose to share.</p>
      </main>
    </div>
  ); // prettier-ignore
}

function Shown({ p, token }: { p: PublicPassport; token: string }) {
  const c = p.contents;
  const groups = MISSIONS.map((m) => ({ m, items: (c.protections ?? []).filter((x) => x.mission === m.id) })).filter((g) => g.items.length); // prettier-ignore
  return (
    <>
      <section className="bcard pb-card" aria-label={`Cyber passport of ${c.business}`}>
        <p className="pp-for">Shared with <b>{p.audience}</b> on {day(p.shared_at)}, until {day(p.expires_at)}{p.purpose ? `: ${p.purpose}` : '.'}</p>
        <BadgeCard business={c.business} score={c.score} band={c.band} verified={c.verified} date={c.scored_at} />
        <ul className="pb-facts">
          <li><BadgeCheck size={16} /> A CyberScore runs from {SCORE_MIN} to {SCORE_MAX}. {c.score} is in the {BAND_LABEL[c.band]} band.</li>
          <li><BadgeCheck size={16} /> {Math.round(c.verified * 100)}% of it is verified from evidence or connected systems; the rest is what the business declared.</li>
          {c.grade && <li><BadgeCheck size={16} /> Evidence grade {c.grade.label}</li>}
          <li><BadgeCheck size={16} /> As it stood on {day(c.scored_at)}. The business’s security may have changed since.</li>
        </ul>
      </section>

      {groups.length > 0 && (
        <section className="bcard pp-card" aria-labelledby="pp-protections">
          <h2 id="pp-protections">Protections</h2>
          <p className="muted small">What the business answered for each protection, and how it is backed. “Checked” means a document, a scan or a connected system backs it, not only the business’s word.{c.not_answered ? ` ${c.not_answered} more protection${c.not_answered === 1 ? ' was' : 's were'} not answered and ${c.not_answered === 1 ? 'is' : 'are'} not listed.` : ''}</p>
          {groups.map(({ m, items }) => (
            <div key={m.id} className="pp-group">
              <h3>{m.name}</h3>
              <ul>{items.map((x) => <Protection key={x.control} x={x} />)}</ul>
            </div>
          ))}
        </section>
      )}

      {p.documents.length > 0 && (
        <section className="bcard pp-card" aria-labelledby="pp-documents">
          <h2 id="pp-documents">Documents</h2>
          <ul className="pp-docs">
            {p.documents.map((d) => (
              <li key={d.id}>
                <FileText size={16} aria-hidden />
                <span className="grow">{d.name} <span className="muted small">{size(d.size_bytes)}</span></span>
                <a className="bbtn sm soft" href={passportDocumentUrl(token, d.id)} download={d.name}><Download size={14} /> Download</a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  ); // prettier-ignore
}

function Protection({ x }: { x: PassportProtection }) {
  const st = STATUS[x.status];
  return (
    <li className="pp-row">
      <span className="grow">{x.name}</span>
      <span className="muted small">
        {x.provenance ? PROVENANCE_LABEL[x.provenance] : ''}
        {x.checked && <b className="ok-t"> · Checked</b>}
      </span>
      <span className={`bbadge nodot ${st.tone}`}>{st.label}</span>
    </li>
  );
}
